export type Exam = 'HSA' | 'TSA' | 'VACT'
export type QuestionType = 'single-choice' | 'numeric' | 'multi-select' | 'true-false'

/** These keys, rather than display strings, are the analytics contract. */
export const KNOWLEDGE = {
  arithmetic: 'Số học',
  functions: 'Hàm số & Đồ thị',
  'exponents-logarithms': 'Mũ & Logarit',
  calculus: 'Nguyên hàm & Tích phân',
  'complex-numbers': 'Số phức',
  algebra: 'Đại số & Phương trình',
  sequences: 'Dãy số & Cấp số',
  trigonometry: 'Lượng giác',
  'planar-geometry': 'Hình học phẳng',
  'spatial-geometry': 'Hình học không gian',
  oxyz: 'Hình học Oxyz',
  'probability-statistics': 'Xác suất & thống kê',
  data: 'Dữ liệu',
  logic: 'Logic',
  modelling: 'Mô hình hóa',
} as const

export type KnowledgeCode = keyof typeof KNOWLEDGE

export const COMPETENCIES = {
  calculation: 'Tính toán',
  'quantitative-reasoning': 'Suy luận định lượng',
  'logical-reasoning': 'Suy luận logic',
  'data-analysis': 'Đọc & phân tích dữ liệu',
  modelling: 'Mô hình hóa',
  'problem-solving': 'Giải quyết vấn đề',
  'decision-making': 'Ra quyết định',
  'pattern-recognition': 'Nhận dạng quy luật',
} as const

export type CompetencyCode = keyof typeof COMPETENCIES

export const MISCONCEPTIONS = {
  'concept-gap': 'Chưa nắm kiến thức nền',
  'condition-reversal': 'Đảo điều kiện / suy luận ngược',
  'data-reading': 'Đọc dữ liệu hoặc đơn vị sai',
  'calculation-slip': 'Tính toán hoặc dấu sai',
  'strategy-choice': 'Chọn hướng giải chưa phù hợp',
  'time-pressure': 'Biết cách nhưng chưa kịp thời gian',
  'careless-reading': 'Đọc sót từ khóa của đề',
  unknown: 'Chưa xác định được nguyên nhân',
} as const

export type MisconceptionCode = keyof typeof MISCONCEPTIONS
export type Confidence = 'high' | 'medium' | 'low'
export type AnswerValue = string | string[] | boolean[]

export type Choice = { id: string; text: string }

/**
 * A visual that belongs to a question. The question JSON stores only this
 * durable reference; the rendered SVG/PNG lives in the question-assets R2
 * bucket and is looked up through the Worker.
 */
export type QuestionAsset = {
  assetId: string
  kind: 'diagram' | 'chart' | 'image'
  placement: 'passage' | 'stem' | 'solution'
  alt: string
  caption?: string
}

export type Question = {
  id: string
  /** Public, stable trace code. The legacy id remains the persistence key. */
  contentCode?: string
  exam: Exam
  /** Kept for backwards-compatible display; use knowledge for analytics. */
  domain: string
  topic: string
  knowledge: KnowledgeCode[]
  competency: CompetencyCode[]
  difficulty: 1 | 2 | 3 | 4 | 5
  cognitiveLevel: 'M1' | 'M2' | 'M3'
  type: QuestionType
  passageTitle?: string
  passageText?: string
  /** Optional visual references. Never store base64 or raw SVG in question JSON. */
  assets?: QuestionAsset[]
  stem: string
  choices?: Choice[]
  answer: AnswerValue
  solution: string
  hint1: string
  hint2: string
  estimatedSeconds: number
  tags: string[]
  /** Only author-created variants share this key. Existing items default to themselves. */
  variantGroup: string
  prerequisites: KnowledgeCode[]
  misconceptionOptions: MisconceptionCode[]
  /** System-injected context for LLM AI Tutors to avoid hallucination when answering questions about this generated variant. */
  aiContext?: string
}

/** Source question shape accepted while legacy question files are progressively migrated. */
export type LegacyQuestion = Omit<Question, 'knowledge' | 'competency' | 'variantGroup' | 'prerequisites' | 'misconceptionOptions'> & {
  competency: string[]
  knowledge?: (KnowledgeCode | 'geometry')[]
  variantGroup?: string
  prerequisites?: (KnowledgeCode | 'geometry')[]
  misconceptionOptions?: MisconceptionCode[]
}

export type Attempt = {
  id: string
  questionId: string
  answer: AnswerValue
  correct: boolean
  timeSeconds: number
  competency: CompetencyCode[]
  knowledge: KnowledgeCode[]
  topic: string
  variantGroup: string
  prerequisites: KnowledgeCode[]
  confidence: Confidence
  misconception?: MisconceptionCode
  hintLevel: number
  session: 'practice' | 'mirror-sprint' | 'exam' | 'arena'
  at: string
  reviewDueAt?: string
  /** The secured practice Edge Function has already written this attempt. */
  serverPersisted?: boolean
}

export type AttemptUpdate = Pick<Attempt, 'misconception'>

export type AppUser = {
  uid: string
  displayName: string
  nickname: string
  avatar: string
  email: string
  provider: 'firebase' | 'supabase' | 'demo'
  /** Comes from trusted backend claims where roles are enabled; never trust a client-entered role. */
  role: 'student' | 'parent' | 'teacher' | 'admin'
}
